/**
 * Component Showcase for Visual Regression Testing
 *
 * This page displays all component variants in one place for visual testing.
 * Used by Playwright to capture screenshots and detect visual regressions.
 *
 * Supports i18n testing via ?lng=de query parameter
 */

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { AlertCircle, CheckCircle2, Info, Upload, Trash2 } from "lucide-react";

export default function ComponentShowcase() {
  const { t } = useTranslation();
  return (
    <div className="container mx-auto p-8 space-y-12 max-w-6xl">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">Component Showcase</h1>
        <p className="text-muted-foreground">
          Visual reference for all design system components
        </p>
      </div>

      <Separator />

      {/* Buttons Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Buttons</h2>
          <p className="text-sm text-muted-foreground">Button variants and sizes</p>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-3">Variants with Real Text</h3>
            <div className="flex flex-wrap gap-4">
              <Button variant="default">
                <Upload className="mr-2 h-4 w-4" />
                {t("uploadArea.uploadTitle")}
              </Button>
              <Button variant="secondary">{t("uploadArea.nextPage")}</Button>
              <Button variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                {t("uploadArea.analysisFailed")}
              </Button>
              <Button variant="outline">{t("uploadArea.previousPage")}</Button>
              <Button variant="ghost">{t("navigation.profile")}</Button>
              <Button variant="link">{t("navigation.stores")}</Button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-3">Longer Text (German stress test)</h3>
            <div className="flex flex-wrap gap-4">
              <Button>
                {t("uploadArea.dragAndDrop").substring(0, 40)}...
              </Button>
              <Button variant="outline">
                {t("uploadArea.convertingPdf")}
              </Button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-3">Sizes</h3>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="sm">{t("uploadArea.previousPage")}</Button>
              <Button size="default">{t("uploadArea.uploadTitle")}</Button>
              <Button size="lg">{t("uploadArea.nextPage")}</Button>
              <Button size="icon">
                <Info className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-3">States</h3>
            <div className="flex flex-wrap gap-4">
              <Button>{t("uploadArea.analyzingReceipt")}</Button>
              <Button disabled>{t("uploadArea.analysisFailed")}</Button>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* Cards Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Cards</h2>
          <p className="text-sm text-muted-foreground">Card components and layouts</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("stores.storeInformation")}</CardTitle>
              <CardDescription>{t("stores.businessInformation")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-medium">{t("stores.totalSpend")}:</span> €1,234.56
                </p>
                <p className="text-sm">
                  <span className="font-medium">{t("stores.receiptCount", { count: 15 })}</span>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("uploadArea.analysisComplete")}</CardTitle>
              <CardDescription>
                {t("uploadArea.foundItems", { count: 8, storeName: "Supermarché" })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {t("receiptCard.topCategories")}: {t("category.food")}, {t("category.household")}
              </p>
            </CardContent>
            <CardFooter className="gap-2">
              <Button variant="outline" size="sm">
                {t("buttons.uploadAnother")}
              </Button>
              <Button size="sm">{t("buttons.viewDetails")}</Button>
            </CardFooter>
          </Card>
        </div>
      </section>

      <Separator />

      {/* Form Elements Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Form Elements</h2>
          <p className="text-sm text-muted-foreground">Input fields and form controls</p>
        </div>

        <div className="space-y-6 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="text-input">{t("uploadArea.uploadTitle")}</Label>
            <Input id="text-input" placeholder={t("uploadArea.dragAndDrop")} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="disabled-input">{t("uploadArea.processingPdf")}</Label>
            <Input id="disabled-input" disabled placeholder={t("uploadArea.convertingPdf")} />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="checkbox" />
            <Label htmlFor="checkbox">
              {t("auth.iAcceptThe")} {t("auth.privacyPolicy")} {t("auth.andThe")} {t("auth.eula")}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch id="switch" />
            <Label htmlFor="switch">{t("profile.languagePreferencesDescription")}</Label>
          </div>

          <div className="space-y-2">
            <Label>Slider</Label>
            <Slider defaultValue={[50]} max={100} step={1} className="w-full" />
          </div>

          <div className="space-y-2">
            <Label>Progress</Label>
            <Progress value={65} className="w-full" />
          </div>
        </div>
      </section>

      <Separator />

      {/* Badges Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Badges</h2>
          <p className="text-sm text-muted-foreground">Status indicators and labels</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Badge variant="default">{t("categories.food")}</Badge>
          <Badge variant="secondary">{t("categories.household")}</Badge>
          <Badge variant="destructive">{t("uploadArea.analysisFailed")}</Badge>
          <Badge variant="outline">{t("categories.health")}</Badge>
          <Badge>{t("categories.personal")}</Badge>
          <Badge variant="secondary">{t("receiptCard.items", { count: 12 })}</Badge>
        </div>
      </section>

      <Separator />

      {/* Alerts Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Alerts</h2>
          <p className="text-sm text-muted-foreground">Contextual feedback messages</p>
        </div>

        <div className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>{t("uploadArea.cloudNotEnabled")}</AlertTitle>
            <AlertDescription>
              {t("uploadArea.supportedFormats")}
            </AlertDescription>
          </Alert>

          <Alert variant="default">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>{t("uploadArea.analysisComplete")}</AlertTitle>
            <AlertDescription>
              {t("uploadArea.foundItems", { count: 12, storeName: "Supermarkt" })}
            </AlertDescription>
          </Alert>

          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("uploadArea.analysisFailed")}</AlertTitle>
            <AlertDescription>
              {t("uploadArea.couldNotAnalyze")} - {t("uploadArea.invalidFileTypeDescription")}
            </AlertDescription>
          </Alert>
        </div>
      </section>

      <Separator />

      {/* Typography Section */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Typography</h2>
          <p className="text-sm text-muted-foreground">Text styles and hierarchy</p>
        </div>

        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Heading 1</h1>
          <h2 className="text-3xl font-semibold">Heading 2</h2>
          <h3 className="text-2xl font-semibold">Heading 3</h3>
          <h4 className="text-xl font-medium">Heading 4</h4>
          <p className="text-base">Body text - regular paragraph with normal weight</p>
          <p className="text-sm text-muted-foreground">
            Small text - often used for captions and secondary information
          </p>
          <p className="text-xs text-muted-foreground">
            Extra small text - used for fine print and metadata
          </p>
        </div>
      </section>
    </div>
  );
}
